import { RemoveOutlined } from '@mui/icons-material';

import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';

import { useState } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { nonFalsey } from '@/utils/nonFalsey';

import { RemoveSignatureDialog } from './unsign-file-menu-item/RemoveSignatureDialog';

const UNSIGN_FILE_MUTATION = graphql(`
  mutation UnsignFile($data: UnsignFileInput) {
    unsignFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

export const UNSIGN_FILE_MENU_ITEM_FILE_FRAGMENT = graphql(`
  fragment unsignFileMenuItemFileFields on File {
    ...fileFields
  }
`);

interface Props {
  file: DocumentType<typeof UNSIGN_FILE_MENU_ITEM_FILE_FRAGMENT>;
  canUnsignCount: number;
  onClose: () => void;
}

export const UnsignFileMenuItem = ({
  file,
  canUnsignCount,
  onClose,
}: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { filename, signatures } = file;

  const unsignableSignatures =
    signatures
      ?.filter((signature) => signature?.userCanUnsign)
      ?.filter(nonFalsey) ?? [];

  const signatureId =
    unsignableSignatures.length === 1 ? unsignableSignatures[0]?._id : null;
  const [unsignFile] = useMutation(UNSIGN_FILE_MUTATION, {
    errorMessage: `Couldn't mark "${filename}" as unsigned`,
    optimisticResponse: {
      unsignFile: {
        __typename: 'UnsignFileResult',
        file: {
          ...file,
          signatures:
            signatures?.filter((signature) => signature?._id !== signatureId) ??
            [],
        },
      },
    },
  });

  return (
    <>
      <MenuItem
        onClick={async () => {
          onClose();

          if (signatureId) {
            await unsignFile({
              variables: { data: { fileSignature: signatureId } },
            });
          } else {
            setDialogOpen(true);
          }
        }}
      >
        <ListItemIcon>
          <RemoveOutlined />
        </ListItemIcon>
        <ListItemText
          primary={
            canUnsignCount === 1 ? 'Mark as unsigned' : 'Remove a signature...'
          }
        />
      </MenuItem>
      <RemoveSignatureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onUnsign={async (signatureId: string) => {
          await unsignFile({
            variables: {
              data: {
                fileSignature: signatureId,
              },
            },
          });
        }}
        file={file}
      />
    </>
  );
};
